import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HomeV2Layout, useTimeOfDay } from '../components/home-v2';
import { useCheckInTransition, CheckInTransitionOverlay } from '../components/home-v2/check-in-transition/index.js';
import { buildLibraryGradient } from '../components/home-v2/HomeV2Background';
import { HorizontalActivityCarousel } from '../components/library';
import PackAccordionRow from '../components/library/PackAccordionRow';
import { usePacks } from '../hooks/usePacks';
import { useInfiniteBookmarks } from '../hooks/useBookmarks';
import { useHomeRecommendations } from '../hooks/useHomeRecommendations';
import { containerVariants, itemVariants } from '../lib/animations';
import { sectionPadding, sectionHeading, skeletonBg } from '../components/library/constants';

function PackAccordionSkeleton() {
  return (
    <div className="flex flex-col gap-[8px]">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className={`h-[56px] md:h-[60px] lg:h-[64px] rounded-[16px] ${skeletonBg} animate-pulse`} />
      ))}
    </div>
  );
}

export default function LibraryPage() {
  const { config } = useTimeOfDay();
  const libraryBg = buildLibraryGradient(config);
  const {
    isTransitioning,
    cloudRect,
    startTransition,
    onTransitionComplete,
    resetTransition,
  } = useCheckInTransition();
  const { data: packsData, isLoading: packsLoading } = usePacks();
  const {
    data: bookmarkData,
    isLoading: bookmarksLoading,
    fetchNextPage: fetchMoreBookmarks,
    hasNextPage: hasMoreBookmarks,
    isFetchingNextPage: isFetchingMoreBookmarks,
  } = useInfiniteBookmarks(10);

  const localHour = useMemo(() => new Date().getHours(), []);

  const {
    data: recommendationsData,
    isLoading: isLoadingRecs,
  } = useHomeRecommendations({
    localHour,
    ageRanges: undefined,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const packs = packsData ?? [];
  const bookmarks = bookmarkData?.pages.flatMap((p) => p.bookmarks) ?? [];
  const totalBookmarks = bookmarkData?.pages[0]?.pagination.totalCount ?? 0;

  const fallbackActivity = useMemo(() => {
    const first = recommendationsData?.activities?.[0];
    if (!first) return [];
    return [{
      id: first.id,
      title: first.title,
      description: first.description ?? '',
      imageUrl: first.imageUrl,
      duration: first.duration,
      durationText: first.durationText,
      playType: first.playType,
      playTypeText: first.playTypeText,
      prepNeeded: first.prepNeeded,
      prepNeededText: first.prepNeededText,
      likesCount: first.statistics?.thumbsUpCount ?? 0,
      whyItWorks: first.whyItWorks
        ? { text: first.whyItWorks.text, boldWords: first.whyItWorks.boldKeywords }
        : null,
      categoryColor: first.categoryColor ?? null,
    }];
  }, [recommendationsData]);

  const [expandedPackId, setExpandedPackId] = useState<number | null>(null);

  useEffect(() => {
    if (packsData && packsData.length > 0 && expandedPackId === null) {
      setExpandedPackId(packsData[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packsData]);

  const handlePackToggle = useCallback((packId: number) => {
    setExpandedPackId((prev) => (prev === packId ? null : packId));
  }, []);

  return (
    <>
    <HomeV2Layout
      timeOfDayConfig={config}
      backgroundGradient={libraryBg}
      contentStyle="transparent"
      transparentContainerXPadding={0}
      activeTab="library"
      showNightDecorations={false}
      showMorningGlow={false}
      showCloudLogo
      showAvatar={false}
      onCloudClick={startTransition}
      isTransitioning={isTransitioning}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="pt-6"
      >
        {/* "Your List" section — shows bookmarks or first recommended activity */}
        <motion.div variants={itemVariants} className="mb-8">
          <div className={`flex items-center justify-between ${sectionPadding} mb-4`}>
            <h2
              className={sectionHeading}
              style={{ fontFamily: 'DM Sans, sans-serif', letterSpacing: '-1.28px' }}
            >
              Your Sparks
            </h2>
            {/* {totalBookmarks > 0 && ( */}
              <Link
                to="/library/all"
                className="text-[12px] font-black uppercase text-[#4FAACC]"
                style={{ fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.72px' }}
              >
                VIEW ALL
              </Link>
            {/* )} */}
          </div>
          {totalBookmarks > 0 || bookmarksLoading ? (
            <HorizontalActivityCarousel
              activities={bookmarks.map((b) => ({
                id: b.activity.id,
                title: b.activity.title,
                description: b.activity.description ?? '',
                imageUrl: b.activity.imageUrl,
                duration: b.activity.duration,
                durationText: b.activity.durationText,
                playType: b.activity.playType,
                playTypeText: b.activity.playTypeText,
                prepNeeded: b.activity.prepNeeded,
                prepNeededText: b.activity.prepNeededText,
                likesCount: b.activity.likesCount,
                whyItWorks: b.activity.whyItWorks,
                categoryColor: b.activity.categoryColor ?? null,
              }))}
              isLoading={bookmarksLoading}
              hasNextPage={hasMoreBookmarks}
              isFetchingNextPage={isFetchingMoreBookmarks}
              onLoadMore={fetchMoreBookmarks}
            />
          ) : (
            <HorizontalActivityCarousel
              activities={fallbackActivity}
              isLoading={isLoadingRecs}
            />
          )}
        </motion.div>

        {/* "Explore what you can do" section */}
        <motion.div variants={itemVariants} className={`${sectionPadding} pb-8`}>
          <h2
            className={`${sectionHeading} mb-4`}
            style={{ fontFamily: 'DM Sans, sans-serif', letterSpacing: '-1.28px' }}
          >
            Explore what you can do
          </h2>
          {packsLoading ? (
            <PackAccordionSkeleton />
          ) : (
            <div className="flex flex-col gap-[8px]">
              {packs.map((pack) => (
                <PackAccordionRow
                  key={pack.id}
                  pack={pack}
                  isExpanded={expandedPackId === pack.id}
                  onToggle={() => handlePackToggle(pack.id)}
                />
              ))}
            </div>
          )}
        </motion.div>
      </motion.div>
    </HomeV2Layout>

      {isTransitioning && (
        <CheckInTransitionOverlay
          cloudRect={cloudRect}
          onComplete={onTransitionComplete}
          onClose={resetTransition}
        />
      )}
    </>
  );
}
